/**
 * Build Cost Estimator API seam (build plan v4).
 *
 * Modal engine (same host as feasibility, `config.modalFeasibilityBase`): /ifc/parse (multipart),
 * /spec/infer, /sow/generate, /abnormals/detect, /narrative/generate, /buildcost/pdf — all
 * synchronous (no spawn/poll). The Xano deterministic cost calc + artifact store go through the
 * normal `api` seam (the `buildcost` group). All endpoints are public.
 */

import { api } from "@/api/client";
import { config } from "@/config/env";

const modalBase = config.modalFeasibilityBase;

export type ExportMethod = "layer_diff" | "phased";

export interface NewSpace {
  name?: string | null;
  long_name?: string | null;
  area: number;
  floor_covering: string;
  is_ground_floor: boolean;
}

export interface OversizedWindow {
  name?: string | null;
  area: number;
}

export interface UnclassifiedDoor {
  name?: string | null;
  width_mm: number;
  operation_type?: string | null;
  note: string;
}

export interface PhaseQuantities {
  floor_area_gia?: number | null;
  ground_floor_area_gia?: number | null;
  external_wall_area?: number | null;
  internal_wall_area?: number | null;
  roof_plan_area?: number | null;
  window_count?: number | null;
  window_count_small?: number | null;
  window_count_medium?: number | null;
  window_count_large?: number | null;
  external_door_count?: number | null; // total external doors (sum of banded external_doors_* counts)
  internal_door_count?: number | null;
  external_doors_single_count?: number | null;
  external_doors_french_small_count?: number | null;
  external_doors_french_large_count?: number | null;
  external_doors_sliding_small_count?: number | null;
  external_doors_sliding_large_count?: number | null;
  external_doors_bifold_small_count?: number | null;
  external_doors_bifold_medium_count?: number | null;
  external_doors_bifold_large_count?: number | null;
  roof_light_count?: number | null;
  has_kitchen?: boolean | null;
  has_bathroom?: boolean | null;
  storey_count?: number | null;
  roof_construction?: string | null;
  roof_construction_confidence?: string | null;
}

export interface IfcQuantities {
  job_id?: string | null;
  parsed_at: string;
  source_file: string;
  export_method: ExportMethod;
  phases_detected: string[];
  quantities: Record<string, PhaseQuantities>;
  ifc_classes: string[];
  gia_source?: "spaces" | "roof_proxy" | null;
  new_spaces?: NewSpace[];
  oversized_windows?: OversizedWindow[];
  unclassified_doors?: UnclassifiedDoor[];
  /** rate_key -> display-label override (e.g. French doors with sidelights). Label only — rate unchanged. */
  door_label_overrides?: Record<string, string>;
  detected_roof_type?: "flat_warm" | "pitched_trussed" | "pitched_cut" | null;
  phase_detection_summary: Record<string, Record<string, number>>;
  warnings: string[];
  extraction_confidence: "high" | "medium" | "low";
}

export interface AxisInference {
  value: string;
  confidence: "high" | "medium" | "low";
  basis: string;
}
export interface SpecInference {
  spec_selections: Record<string, string>;
  detail: Record<string, AxisInference>;
  warnings: string[];
  needs_manual_review: boolean;
}

export interface SowItem {
  ref: string;
  description: string;
  quantity: number | null;
  unit: string;
  source: "measured" | "drafted";
}
export interface SowSection {
  number: number;
  title: string;
  items: SowItem[];
}
export interface Sow {
  sections: SowSection[];
  basis: string;
  exclusions: string[];
}

export interface CostElement {
  element: string;
  label: string;
  quantity: number;
  unit: string;
  base_rate: number;
  multiplier: number;
  cost: number;
}
export interface CostPlan {
  generated_at: string | number;
  currency: string;
  spec_selections: Record<string, string>;
  elements: CostElement[];
  element_subtotal: number;
  demolition_subtotal?: number;
  preliminaries: number;
  preliminaries_pct: number;
  contingency: number;
  contingency_pct: number;
  total: number;
  vat_rate?: number;
  vat?: number;
  total_inc_vat?: number;
  basis: string;
}

export interface Abnormal {
  risk: string;
  likelihood: string;
  reason: string;
  cost_indicative: string;
}
export interface Abnormals {
  abnormals: Abnormal[];
  warnings: string[];
}

export interface NarrativeResult {
  narrative: string;
  validated: boolean;
  warnings: string[];
}

/** Cost-element display order, following the SoW trade sections (mirrors the engine's
 *  cost_inputs.COST_ELEMENT_ORDER). The calc endpoint emits elements in rate-table order, so the
 *  breakdown sorts by this to read in SoW order. Unknown/legacy elements sort to the end. */
export const COST_ELEMENT_ORDER: string[] = [
  "demolition_wall", "demolition_roof_solid", "demolition_roof_conservatory",
  "demolition_window", "demolition_door",
  "substructure",
  "external_walls",
  "roof", "roof_light",
  "windows_small", "windows_medium", "windows_large",
  "external_doors_single", "external_doors_french_small", "external_doors_french_large",
  "external_doors_sliding_small", "external_doors_sliding_large",
  "external_doors_bifold_small", "external_doors_bifold_medium", "external_doors_bifold_large",
  "internal_walls", "internal_wall_finishes", "internal_doors",
  "internal_floor_finishes", "mep", "fittings_kitchen", "fittings_bathroom",
];

export function costElementSortKey(element: string): number {
  const i = COST_ELEMENT_ORDER.indexOf(element);
  return i === -1 ? COST_ELEMENT_ORDER.length : i;
}

/** Spec axes + allowed values — kept in sync with the engine's spec_inference / spec_multiplier. */
export const SPEC_AXES: { key: string; label: string; values: string[] }[] = [
  { key: "construction_method", label: "Construction", values: ["masonry_cavity", "timber_frame", "steel_frame"] },
  { key: "roof_type", label: "Roof type", values: ["flat_warm", "pitched_trussed", "pitched_cut"] },
  { key: "wall_finish", label: "Wall finish", values: ["brick", "render", "cladding", "stone"] },
  { key: "window_spec", label: "Windows", values: ["upvc", "aluminium", "timber"] },
  { key: "finish_tier", label: "Finish tier", values: ["standard", "mid", "premium"] },
];

// --- Modal direct calls -----------------------------------------------------------------

async function modalJson<T>(path: string, body: unknown): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${modalBase}${path}`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify(body),
    });
  } catch {
    throw new Error("Could not reach the build-cost engine (network or CORS issue).");
  }
  if (!res.ok) throw new Error((await res.text().catch(() => "")) || `Request failed (${res.status}).`);
  return (await res.json()) as T;
}

/** POST /ifc/parse — multipart upload; returns the quantities JSON directly (synchronous). */
export async function parseIfc(file: File, exportMethod: ExportMethod, jobId?: number): Promise<IfcQuantities> {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("export_method", exportMethod);
  if (jobId) fd.append("job_id", String(jobId));
  let res: Response;
  try {
    res = await fetch(`${modalBase}/ifc/parse`, { method: "POST", headers: { Accept: "application/json" }, body: fd });
  } catch {
    throw new Error("Could not reach the build-cost engine (network or CORS issue).");
  }
  if (!res.ok) throw new Error((await res.text().catch(() => "")) || `IFC parse failed (${res.status}).`);
  return (await res.json()) as IfcQuantities;
}

export function inferSpec(ifcClasses: string[], sowText = ""): Promise<SpecInference> {
  return modalJson<SpecInference>("/spec/infer", { ifc_classes: ifcClasses, sow_text: sowText });
}

export function generateSow(body: {
  quantities: IfcQuantities;
  spec_selections?: Record<string, string>;
  address?: string;
  application_type?: string;
  extension_type?: string;
  job_id?: number;
  include_kitchen?: boolean;
  include_bathroom?: boolean;
}): Promise<Sow> {
  return modalJson<Sow>("/sow/generate", body);
}

export function detectAbnormals(body: {
  postcode?: string;
  /** Full site address string — grounds the engine's town/area risk reasoning. */
  address?: string;
  /** UPRN — when present the engine treats the address as register-matched (exact),
   *  refining the site risk commentary. */
  uprn?: string;
  property_type?: string;
  application_type?: string;
  constraints?: Record<string, unknown>;
}): Promise<Abnormals> {
  return modalJson<Abnormals>("/abnormals/detect", body);
}

export function generateNarrative(body: {
  cost_plan: CostPlan;
  spec_selections?: Record<string, string>;
  abnormals?: Abnormal[];
  address?: string;
  application_type?: string;
}): Promise<NarrativeResult> {
  return modalJson<NarrativeResult>("/narrative/generate", body);
}

export function buildCostPdf(body: {
  sow?: Sow;
  cost_plan?: CostPlan;
  abnormals?: Abnormal[];
  narrative?: string;
  place?: string;
  client_name?: string;
  pdf_name?: string;
}): Promise<{ pdf_url: string; pdf_name: string }> {
  return modalJson<{ pdf_url: string; pdf_name: string }>("/buildcost/pdf", body);
}

/** POST /buildcost/excel — the 7-tab workbook. Multipart: the IFC is re-parsed for the register
 *  tabs, so the original file is re-sent alongside the already-computed SoW + cost plan. */
export async function buildCostExcel(opts: {
  file: File;
  exportMethod: ExportMethod;
  sow?: Sow;
  costPlan?: CostPlan;
  address?: string;
  jobNumber?: number;
}): Promise<{ xlsx_url: string; xlsx_name: string }> {
  const fd = new FormData();
  fd.append("file", opts.file);
  fd.append("export_method", opts.exportMethod);
  if (opts.sow) fd.append("sow", JSON.stringify(opts.sow));
  if (opts.costPlan) fd.append("cost_plan", JSON.stringify(opts.costPlan));
  if (opts.address) fd.append("address", opts.address);
  if (opts.jobNumber) fd.append("job_number", String(opts.jobNumber));
  let res: Response;
  try {
    res = await fetch(`${modalBase}/buildcost/excel`, { method: "POST", headers: { Accept: "application/json" }, body: fd });
  } catch {
    throw new Error("Could not reach the build-cost engine (network or CORS issue).");
  }
  if (!res.ok) throw new Error((await res.text().catch(() => "")) || `Excel export failed (${res.status}).`);
  return (await res.json()) as { xlsx_url: string; xlsx_name: string };
}

// --- Xano (buildcost group) -------------------------------------------------------------

/** POST /estimate/calculate — deterministic cost plan; stores to job.cost_plan when job_id given. */
export function calculateEstimate(
  quantities: Record<string, number>,
  specSelections: Record<string, string>,
  jobId?: number,
): Promise<CostPlan> {
  return api.post<CostPlan>("buildcost", "estimate/calculate", {
    body: { quantities, spec_selections: specSelections, job_id: jobId ?? 0 },
  });
}

/** Map the parser's new-build quantities onto the cost-element map the calc endpoint expects
 *  (mirrors the engine's feasibility/cost_inputs.py). Only positive quantities are included. */
export function costInputsFromQuantities(
  q: IfcQuantities,
  opts: { phase?: string; includeKitchen?: boolean; includeBathroom?: boolean } = {},
): Record<string, number> {
  const pq = q.quantities[opts.phase ?? "new_build"] ?? {};
  // Floor finishes, M&E and substructure derive from the new floor footprint. Primary source is the
  // FloorCovering-tagged space GIA (gia_source === "spaces"): total GIA → floor finishes + M&E,
  // ground-floor GIA → substructure. When no covered spaces are modelled ("roof_proxy") the roof
  // plan area stands in for all three. Branch on gia_source (mirrors cost_inputs.py) so an upper-
  // floor extension's 0 ground GIA is not masked by the roof.
  let floorMepArea: number;
  let substructureArea: number;
  if (q.gia_source === "spaces") {
    floorMepArea = pq.floor_area_gia ?? 0;
    substructureArea = pq.ground_floor_area_gia ?? 0;
  } else {
    floorMepArea = substructureArea = pq.roof_plan_area ?? 0;
  }
  const kitchen = opts.includeKitchen ?? pq.has_kitchen;
  const bathroom = opts.includeBathroom ?? pq.has_bathroom;
  // Plasterboard area: external wall internal face (×1) + internal wall/partition area once — the
  // plasterboard + skim rate is per m² of wall and already covers both partition faces, so no ×2.
  // No opening deduction.
  const plasterboard = (pq.external_wall_area ?? 0) + (pq.internal_wall_area ?? 0);
  const candidates: Record<string, number> = {
    substructure: substructureArea,
    external_walls: pq.external_wall_area ?? 0,
    roof: pq.roof_plan_area ?? 0,
    roof_light: pq.roof_light_count ?? 0,
    windows_small: pq.window_count_small ?? 0,
    windows_medium: pq.window_count_medium ?? 0,
    windows_large: pq.window_count_large ?? 0,
    // Banded door rates (old single external_doors row retired); internal doors now costed too.
    external_doors_single: pq.external_doors_single_count ?? 0,
    external_doors_french_small: pq.external_doors_french_small_count ?? 0,
    external_doors_french_large: pq.external_doors_french_large_count ?? 0,
    external_doors_sliding_small: pq.external_doors_sliding_small_count ?? 0,
    external_doors_sliding_large: pq.external_doors_sliding_large_count ?? 0,
    external_doors_bifold_small: pq.external_doors_bifold_small_count ?? 0,
    external_doors_bifold_medium: pq.external_doors_bifold_medium_count ?? 0,
    external_doors_bifold_large: pq.external_doors_bifold_large_count ?? 0,
    internal_doors: pq.internal_door_count ?? 0,
    internal_walls: pq.internal_wall_area ?? 0,
    internal_wall_finishes: plasterboard,
    internal_floor_finishes: floorMepArea,
    fittings_kitchen: kitchen ? 1 : 0,
    fittings_bathroom: bathroom ? 1 : 0,
    mep: floorMepArea,
  };

  // Demolition phase → demolition_* elements (the endpoint rolls these into a demolition subtotal).
  const demo = q.quantities.demolition ?? {};
  const demoRoof =
    demo.roof_construction === "conservatory" || demo.roof_construction === "conservatory_suspected"
      ? "demolition_roof_conservatory"
      : "demolition_roof_solid";
  candidates.demolition_wall = (demo.external_wall_area ?? 0) + (demo.internal_wall_area ?? 0);
  candidates[demoRoof] = demo.roof_plan_area ?? 0;
  candidates.demolition_window = demo.window_count ?? 0;
  // Demolition counts all doors removed — external and internal.
  candidates.demolition_door = (demo.external_door_count ?? 0) + (demo.internal_door_count ?? 0);

  const out: Record<string, number> = {};
  for (const [k, v] of Object.entries(candidates)) if (v && v > 0) out[k] = v;
  return out;
}
